<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <!-- 헤더 -->
        <DetailHeader :headerTitle="headerTitle" :backTo="'BizLocations'"/>

        <!-- 바디 -->
        <div class="pt-3 pb-4 d-flex justify-content-center">
          <div v-if="isEdit && loading">
            <TableBodyLoader :minHeight="880" :offsetY="-50" message="상세정보를 불러오는 중..." />
          </div>
          <div v-else class="w-100 px-5">
            <div class="row gx-4">
              <!-- ▼ 좌측: 인풋폼 카드 -->
              <div class="col-lg-6 col-md-12" data-tour="dcreate.left">
                <section class="card border-0 h-100">
                  <div class="card-body">
                      <h6 class="mb-3">상세정보</h6>

                      <!-- 썸네일 -->
                      <ImagePicker
                        class="mb-3"
                        v-model:file="form.imgFile"
                        :existing-preview="form.imgPreview"
                        :existing-name="form.imgFilename"
                        :max-size="10 * 1024 * 1024"
                        label="대표 이미지"
                        alt="workplace"
                        @selected="onImgSelected"
                        @cleared="onImgCleared"
                        @error="toastError"
                      />

                      <!-- 필드 -->
                      <div class="row g-3 mt-1">
                        <div class="col-md-6">
                          <label class="form-label">업무장소명 <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model.trim="form.name" placeholder="예: 본사 1층 로비" />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">URN <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model.trim="form.urn" placeholder="예: urn:epc:id:sgln:..." />
                        </div>

                        <div class="col-md-12">
                          <label class="form-label">주소 <span class="text-danger">*</span></label>
                          <input type="text" class="form-control" v-model.trim="form.address" placeholder="도로명 주소" />
                        </div>

                        <div class="col-md-12" data-tour="dcreate.croods">
                          <div class="row">
                            <div class="col-md-6">
                              <label class="form-label">위도</label>
                              <input type="text" class="form-control" :value="form.lat || ''" readonly />
                            </div>
                            <div class="col-md-6">
                              <label class="form-label">경도</label>
                              <input type="text" class="form-control" :value="form.lng || ''" readonly />
                            </div>
                          </div>
                        </div>

                        <div class="col-12">
                          <label class="form-label">비고</label>
                          <textarea class="form-control" rows="3" v-model.trim="form.dsc" placeholder="메모를 입력하세요."></textarea>
                        </div>
                      </div>

                      <!-- 메타 -->
                      <div class="row g-3 mt-1" v-if="isEdit">
                        <div class="col-md-6">
                          <label class="form-label">생성일시</label>
                          <input type="text" class="form-control" :value="formatDate(form.createdAt) || '-'" disabled />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label">수정일시</label>
                          <input type="text" class="form-control" :value="formatDate(form.updatedAt) || '-'" disabled />
                        </div>
                      </div>
                  </div>
                </section>
              </div>

              <!-- ▼ 우측: 지도 카드 -->
              <div class="col-lg-6 col-md-12 mt-3 mt-lg-0" data-tour="dcreate.right">
                <section class="card h-100 border-0">
                  <div class="card-body">
                    <h6 class="mb-3">위치정보</h6>

                    <MapPicker
                      v-model="locationValue"
                      :center="mapCenter"
                      :zoom="13"
                    />

                    <div class="py-2">
                      <label class="form-label">
                        지도를 클릭하면 해당 위치로 좌표가 선택됩니다. 다시 클릭하면 변경됩니다.
                      </label>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <!-- ▼ 액션 버튼 (카드 바깥) -->
            <div class="d-flex justify-content-between mt-5 mb-3 gap-2 px-2">
              <soft-button
                color="outline-secondary"
                @click="$router.push({ name: 'BizLocations', query: { ...$route.query } })"
              >목록</soft-button>

              <div class="d-flex gap-2">
                <soft-button v-if="isEdit" color="danger" @click="remove" :disabled="loading">삭제</soft-button>
                <soft-button color="success" @click="save" :disabled="loading" data-tour="dcreate.save">저장</soft-button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
  <BizLocationTour />
  <SoftConfirm ref="confirm" />
</template>


<script>
import SoftConfirm from "@/components/SoftConfirm.vue";
import DetailHeader from "@/components/DetailHeader.vue";
import SoftButton from "@/components/SoftButton.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import MapPicker from "@/components/map/mapPicker.vue";
import ImagePicker from "@/views/components/common/ImagePicker.vue";
import BizLocationTour from "@/components/tour/BizLocationTour.vue";
import api from "@/api/api";

export default {
  name: "WorkplaceDetail",
  components: { SoftConfirm, DetailHeader, SoftButton, TableBodyLoader, MapPicker, ImagePicker, BizLocationTour },
  inject: ['showLoading', 'hideLoading'],
  props: {
    mode: { type: String, default: "create" }, // 'create' | 'edit'
    id: { type: [String, Number], default: "" } // edit일 때 식별자
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        name: "",
        address: "",
        urn: "",
        dsc: "",
        mfId: "",
        atchFileId: "",
        imgFile: null,     // 업로드 파일
        imgPreview: "",    // 미리보기 URL
        imgFilename: "",
        lat: null,
        lng: null,
        createdAt: "",
        updatedAt: "",
      },
      imgChanged: false,       // ✅ 새 파일 선택했는지
      imgRemoved: false, 
    };
  },
  computed: {
    isEdit() {
      return this.mode === "edit" && !!this.id;
    },
    userInfo() {
      return this.$store.getters["UserStore/getUserInfo"];
    },
    headerTitle() {
      return this.isEdit ? "업무장소 수정" : "업무장소 등록";
    },
    displayFileName() {
      return this.form.imgFile?.name || this.form.imgFilename || "";
    },
    mapCenter() {
      if (this.form.lat && this.form.lng) {
        return [Number(this.form.lat), Number(this.form.lng)];
      }
      const mfInfo = this.$store.getters["UserStore/getMfInfo"];
      if (mfInfo?.lat && mfInfo?.lng) {
        return [mfInfo.lat, mfInfo.lng];
      }
      return [35.8714354, 128.601445]; // 기본 좌표
    },
    locationValue: {
      get() {
        if (this.form.lat != null && this.form.lng != null) {
          return [Number(this.form.lat), Number(this.form.lng)];
        }
        return null;
      },
      set(val) {
        if (Array.isArray(val) && val.length === 2) {
          this.form.lat = Number(val[0]).toFixed(5);
          this.form.lng = Number(val[1]).toFixed(5);
        } else {
          this.form.lat = null;
          this.form.lng = null;
        }
      }
    }
  },
  async mounted() {
    this.form.mfId = this.userInfo.mfId;
    if (this.isEdit) await this.fetchDetail();
    else this.prefillUrnIfCreate();
    
    this._tourImgHandler = (e) => {
      const { file, blob, name } = e.detail || {};
      if (!file || !blob) return;

      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      const previewUrl = URL.createObjectURL(blob);

      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name || file.name || 'demo.jpg';

      this.imgChanged = true;
      this.imgRemoved = false;
    };
    window.addEventListener('tour:setImage', this._tourImgHandler);
  },
  beforeUnmount() {
    if (this.form.imgPreview) URL.revokeObjectURL(this.form.imgPreview);
    window.removeEventListener('tour:setImage', this._tourImgHandler);
    if (this.form.imgPreview?.startsWith('blob:')) URL.revokeObjectURL(this.form.imgPreview);
  },
  methods: {
    prefillUrnIfCreate() {
      if (this.form.urn && this.form.urn.trim()) return;
      const mfInfo = this.$store.getters['UserStore/getMfInfo'];
      const companyPrefix = mfInfo?.companyPrefix
        ?? this.$store.getters['UserStore/getMfId']
        ?? '';
      if (!companyPrefix) return; 

      this.form.urn = `urn:epc:id:sgln:${companyPrefix}.`;
    },
    async fetchDetail() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get(`/bizLocation`, { params: { id: this.id } }),
          new Promise(r => setTimeout(r, 300))
        ]);

        const item  = res.data?.result?.bizLocation || {};
        const files = res.data?.result?.files || [];

        Object.assign(this.form, item);

        if (files.length > 0) {
          const f = files[0];
          this.form.imgPreview  = f.downloadUrl || "";
          this.form.imgFilename = f.originalName || "";
          this.form.atchFileId  = f.atchFileId || "";
        } else {
          this.form.imgPreview  = "";
          this.form.imgFilename = "";
          this.form.atchFileId  = "";
        }
        this.files = files;
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    onImgSelected({ file, previewUrl, name }) {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name;

      this.imgChanged = true;
      this.imgRemoved = false;
    },
    onImgCleared() {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = null;
      this.form.imgPreview  = "";
      this.form.imgFilename = "";

      this.imgChanged = false;
      this.imgRemoved = true;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("name", this.form.name ?? "");
      fd.append("address", this.form.address ?? "");
      if (this.form.urn) fd.append("urn", this.form.urn);
      if (this.form.dsc) fd.append("dsc", this.form.dsc);
      if (this.form.mfId != null) fd.append("mfId", String(this.form.mfId));

      if (this.form.atchFileId) fd.append("atchFileId", this.form.atchFileId);
      fd.append("imgChanged", this.imgChanged ? "Y" : "N");
      fd.append("imgRemoved", this.imgRemoved ? "Y" : "N");
      if (this.form.imgFile) fd.append("file", this.form.imgFile);

      fd.append("lat", String(this.form.lat));
      fd.append("lng", String(this.form.lng));
      return fd;
    },
    async save() {
      if (!this.form.name?.trim()) return this.toastError?.("업무장소명을 입력하세요.");
      if (!this.form.urn?.trim())  return this.toastError?.("URN을 입력하세요.");
      if (!this.form.address?.trim()) return this.toastError?.("주소를 입력하세요.");
      if (!this.form.lat) return this.toastError?.("지도를 클릭해 위치를 선택하세요.");

      const action = this.isEdit ? '수정' : '등록';
      const ok = await this.$refs.confirm.open({
        title: `업무장소 ${action}`,
        message: `${action} 하시겠습니까?`,
        confirmText: action,
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();

      this.showLoading?.('저장중...');
      try {
        const fd = this.buildFormData();

        if (this.isEdit) {
          await api.put(`/bizLocation/${this.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const { data } = await api.post(`/bizLocation`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.form.id = data?.result?.id;
        }

        const elapsed = performance.now() - started;
        if (elapsed < 1000) await delay(1000 - elapsed);

        this.toastSuccess?.(`업무장소가 ${action}되었습니다.`);
        this.$router.push({
          name: "BizLocationEdit",
          params: { id: this.form.id },
          query: { ...this.$route.query, t: Date.now() } 
        });

      } catch (e) {
        this.toastError?.(`${action} 중 오류가 발생했습니다.`);
      } finally {
        this.hideLoading?.();
      }
    },
    async remove() {
      if (!this.isEdit) return;

      const ok = await this.$refs.confirm.open({
        title: "삭제 확인",
        message: "정말 삭제하시겠습니까?",
        confirmText: "삭제",
        cancelText: "취소",
        confirmColor: "danger",
      });
      if (!ok) return;
  
      this.showLoading?.('삭제중...');
      try {
        const delay = ms => new Promise(r => setTimeout(r, ms));
        await Promise.all([
          api.delete(`/bizLocation/${this.id}`),
          delay(500),
        ]);

        this.toastSuccess?.("삭제되었습니다.");
        this.$router.push({
          name: "BizLocations",
          query: { ...this.$route.query }, 
        });
      } catch (e) {
        this.toastError?.("삭제 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    }
  }
};
</script>

<style scoped>
.card { border: 1px solid rgba(0,0,0,.06) !important; }

.input-group .form-control[readonly] {
  height: 43px;          /* 원하는 높이 */
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;   /* 글씨도 조금 줄임 */
}
</style>
